/**
 * The conditional-display comparison rule (#151).
 *
 * This is one of TWO implementations. The API mirrors it exactly in
 * `ConditionalOperators.Matches` (apps/api/CalcualatorAPI.Domain/Constants/ConditionalOperators.cs)
 * because the server evaluates conditional display independently when it prices a Lookup element.
 * The two must agree on every input, or a shopper is charged a price they never saw — change one
 * and you change the other, in the same PR, with the matching test in both suites.
 *
 * Kept as its own module rather than inlined into ProcessConditionalElements so it can be unit
 * tested directly (tests/unit/conditional-operators.test.ts) instead of only through the browser
 * harness.
 */

/** Operator values as the builder stores them. Anything else reads as "equals". */
const EQUALS = "equals";
const NOT_EQUALS = "not_equals";

/** The four operators that interpret both sides as numbers. */
const ORDERING_OPERATORS = ["greater_than", "less_than", "at_least", "at_most"];

const ALL_OPERATORS = [EQUALS, NOT_EQUALS].concat(ORDERING_OPERATORS);

/**
 * Builder `fieldType` values whose value is a number the shopper types or drags, and which may
 * therefore control an ordering comparison. The legacy "Number" field type is deliberately absent —
 * it is not offered as a trigger.
 */
export const NUMERIC_TRIGGER_TYPES = ["NumberInputNew", "Slider"];

/**
 * Whether a saved condition's recorded trigger type means a number input or slider.
 *
 * A condition saved before #151 carries no type at all, and answers false here. That is the guard
 * that stops a stale reference — left behind when the controlling element was renamed or deleted
 * (issue #191) — from suddenly resolving against a same-named number input and revealing an element
 * that has been hidden on a live product page for months.
 */
export function isNumericTriggerType(triggerType: unknown): boolean {
  return (
    typeof triggerType === "string" && NUMERIC_TRIGGER_TYPES.indexOf(triggerType) !== -1
  );
}

/**
 * Anything unrecognised — including absent, which is every calculator saved before #209 — reads as
 * "equals", preserving the original behaviour.
 */
export function normalizeOperator(operator: unknown): string {
  return typeof operator === "string" && ALL_OPERATORS.indexOf(operator) !== -1
    ? operator
    : EQUALS;
}

/**
 * Does the controller's current value satisfy the condition?
 *
 * Equality is TEXT comparison — unchanged from #209, and deliberately so: making it numeric would
 * start matching "25" against "25.0" and change how existing calculators behave. Ordering is
 * NUMERIC, because both values arrive from the DOM as strings and "10" > "9" is false as text.
 *
 * A side that is not a number makes an ordering comparison false rather than throwing, so a cleared
 * or half-typed field simply keeps the dependent element hidden.
 */
export function matchesCondition(
  operator: unknown,
  controllerValue: unknown,
  conditionValue: unknown,
): boolean {
  var normalized = normalizeOperator(operator);

  if (ORDERING_OPERATORS.indexOf(normalized) === -1) {
    // Loose equality, exactly as the pre-#151 comparison did — the values reaching here are the
    // strings jQuery reads off the control.
    var equal = controllerValue == conditionValue;
    return normalized === NOT_EQUALS ? !equal : equal;
  }

  var left = parseFloat(controllerValue as string);
  var right = parseFloat(conditionValue as string);
  if (isNaN(left) || isNaN(right)) return false;

  if (normalized === "greater_than") return left > right;
  if (normalized === "less_than") return left < right;
  if (normalized === "at_least") return left >= right;
  return left <= right;
}
